import { useEffect, useState } from "react";
import axios from "axios";
import {
  Inbox, Receipt, Banknote, FileWarning, Briefcase, ChevronRight, AlertCircle,
  AlertTriangle, Clock, FileSignature, PenLine,
} from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";

const formatGBP = (n) =>
  `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDateDMY = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

// Dense banking-grade KPI tile — flat, crisp hairline border, no heavy shadow.
const Kpi = ({ label, value, hint, icon: Icon, testid, accent = "gold" }) => (
  <div
    data-testid={testid}
    className={`relative bg-white border border-forest/15 rounded-sm px-4 py-3 transition-colors hover:border-gold/60`}
  >
    <span className={`absolute inset-y-0 left-0 w-[3px] ${accent === "rose" ? "bg-rose-700" : "bg-gold"} opacity-80`} />
    <div className="flex items-start justify-between gap-3 pl-2">
      <div className="min-w-0">
        <p className="text-[10px] tracking-[0.22em] uppercase font-semibold text-ink-muted">{label}</p>
        <p className="font-serif text-[26px] sm:text-[28px] text-forest mt-1 leading-tight truncate" data-testid={`${testid}-value`}>
          {value}
        </p>
        {hint && <p className="text-[11px] text-sage mt-0.5">{hint}</p>}
      </div>
      <span className="shrink-0 w-9 h-9 rounded-sm bg-cream-3 border border-gold/30 text-gold-dark flex items-center justify-center">
        <Icon size={15} strokeWidth={1.7} />
      </span>
    </div>
  </div>
);

const InvoiceStatusPill = ({ status }) => {
  const map = {
    overdue: "bg-rose-50 text-rose-900 border-rose-300",
    unpaid: "bg-amber-50 text-amber-900 border-amber-300",
    part_paid: "bg-sky-50 text-sky-900 border-sky-300",
    paid: "bg-emerald-50 text-emerald-900 border-emerald-300",
    draft: "bg-stone-100 text-stone-800 border-stone-300",
    sent: "bg-sky-50 text-sky-900 border-sky-300",
  };
  const label = {
    overdue: "Overdue", unpaid: "Unpaid", part_paid: "Part Paid",
    paid: "Paid", draft: "Draft", sent: "Sent",
  };
  return (
    <span className={`inline-flex items-center text-[9.5px] tracking-[0.14em] uppercase font-bold px-1.5 py-0.5 border rounded-sm ${map[status] || map.unpaid}`}>
      {label[status] || status || "—"}
    </span>
  );
};

const SeverityIcon = ({ kind }) => {
  if (kind === "overdue_invoice") return <AlertTriangle size={14} className="text-rose-700" />;
  if (kind === "missing_docs")    return <FileWarning size={14} className="text-amber-700" />;
  if (kind === "unsigned_application") return <PenLine size={14} className="text-sky-700" />;
  if (kind === "stale_case")      return <Clock size={14} className="text-stone-600" />;
  return <AlertCircle size={14} className="text-sage" />;
};

export const DashboardSection = ({ onNavigate, me }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setError("");
      const { data } = await axios.get(`${API}/admin/dashboard/summary`, authHeaders());
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "The dashboard didn't load.");
    }
  };

  useEffect(() => { load(); }, []);

  const today = formatDateDMY(new Date().toISOString());
  const firstName = (me?.name || "").split(" ")[0];

  if (error) {
    return (
      <div data-testid="dashboard-error" className="bg-white border border-rose-200 rounded-sm p-8 text-center">
        <AlertCircle className="mx-auto text-rose-500 mb-3" size={28} />
        <p className="text-sm text-rose-700 mb-4">{error}</p>
        <button onClick={load} className="btn-gold inline-flex">Try again</button>
      </div>
    );
  }

  const k = data?.kpis;
  const openAttention = (item) => {
    if (item.kind === "overdue_invoice") return onNavigate?.("invoices", { focusId: item.ref_id });
    if (item.kind === "missing_docs")    return onNavigate?.("customers", { focusId: item.ref_id, tab: "documents" });
    if (item.kind === "unsigned_application") return onNavigate?.("customers", { focusId: item.customer_id, tab: "applications" });
    if (item.kind === "stale_case")      return onNavigate?.("customers", { focusId: item.ref_id, tab: "progress" });
  };

  return (
    <div data-testid="admin-dashboard-section" className="space-y-6">
      {/* Greeting */}
      <header>
        <p className="text-[10px] tracking-[0.26em] uppercase text-gold-dark font-semibold">Back Office</p>
        <h1 className="font-serif text-2xl sm:text-3xl text-forest mt-1.5">
          {firstName ? `Welcome back, ${firstName}` : "Welcome back"}
        </h1>
        <p className="text-[12.5px] text-sage mt-1" data-testid="dashboard-today">
          Today is <span className="font-semibold text-forest">{today}</span>
        </p>
      </header>

      {/* 5 KPI tiles — dense, flat, hairline borders */}
      <section className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        <Kpi testid="kpi-open-cases"           label="Open cases"
             value={k ? k.open_cases : "—"}         hint="Live applications"
             icon={Briefcase} />
        <Kpi testid="kpi-unread-enquiries"     label="New enquiries"
             value={k ? k.unread_enquiries : "—"}   hint="Unread, incl. WhatsApp"
             icon={Inbox} />
        <Kpi testid="kpi-unpaid-invoices"      label="Unpaid invoices"
             value={k ? formatGBP(k.unpaid_total) : "—"}  hint="Total balance due"
             icon={Receipt} accent={k && k.unpaid_total > 0 ? "rose" : "gold"} />
        <Kpi testid="kpi-payments-this-month"  label="Payments this month"
             value={k ? formatGBP(k.payments_this_month) : "—"}  hint="Received since the 1st"
             icon={Banknote} />
        <Kpi testid="kpi-missing-docs"         label="Missing documents"
             value={k ? k.customers_missing_docs : "—"}  hint="Customers to chase"
             icon={FileWarning} />
      </section>

      {/* Case Pipeline strip */}
      <section className="bg-white border border-forest/15 rounded-sm" data-testid="dashboard-pipeline">
        <header className="flex items-center justify-between px-5 py-3 border-b border-forest/10">
          <div>
            <h2 className="font-serif text-lg text-forest">Case pipeline</h2>
            <p className="text-[11.5px] text-sage mt-0.5">Live customers at each stage · click a stage to filter the customer list</p>
          </div>
        </header>
        <div className="flex gap-2 overflow-x-auto px-5 py-4">
          {(data?.pipeline || []).map((s, i) => (
            <button
              key={s.id}
              type="button"
              data-testid={`pipeline-stage-${s.id}`}
              onClick={() => onNavigate?.("customers", { stage: s.id })}
              className={`group flex items-center justify-between gap-3 px-3.5 py-2.5 min-w-[180px] bg-cream-3 border rounded-sm transition-colors ${
                s.count > 0 ? "border-forest/20 hover:border-gold hover:bg-cream-2" : "border-forest/10 hover:border-forest/25"
              }`}
            >
              <div className="min-w-0 text-left">
                <p className="text-[10px] tracking-[0.14em] uppercase font-semibold text-ink-muted">Stage {i + 1}</p>
                <p className="text-[12.5px] font-semibold text-forest mt-0.5 truncate">{s.label}</p>
              </div>
              <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full border ${
                s.count > 0 ? "bg-gold text-forest-deep border-gold" : "bg-white text-ink-muted border-forest/15"
              }`}>{s.count}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Attention list + Latest signed */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 bg-white border border-forest/15 rounded-sm overflow-hidden" data-testid="dashboard-attention">
          <header className="flex items-center justify-between px-5 py-3 border-b border-forest/10">
            <div>
              <h2 className="font-serif text-lg text-forest">Today &amp; attention</h2>
              <p className="text-[11.5px] text-sage mt-0.5">Items needing action · click any row to open the record</p>
            </div>
            {data?.attention?.length > 0 && (
              <span className="text-[10.5px] font-semibold tracking-wider uppercase text-ink-muted">{data.attention.length} items</span>
            )}
          </header>
          <ul className="divide-y divide-forest/5">
            {data?.attention?.length ? (
              data.attention.map((item, i) => (
                <li key={`${item.kind}-${item.ref_id}-${i}`}>
                  <button
                    type="button"
                    data-testid={`attention-${item.kind}-${i}`}
                    onClick={() => openAttention(item)}
                    className={`w-full flex items-center justify-between gap-4 px-5 py-3 text-left transition-colors hover:bg-cream-3 border-l-4 ${
                      item.severity === "high" ? "border-l-rose-700 bg-rose-50/30"
                      : item.severity === "medium" ? "border-l-gold"
                      : "border-l-stone-300"
                    }`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="mt-0.5"><SeverityIcon kind={item.kind} /></span>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-forest truncate">{item.label}</p>
                        <p className="text-[11.5px] text-sage mt-0.5 truncate">{item.sub}</p>
                      </div>
                    </div>
                    <ChevronRight size={14} className="shrink-0 text-sage" />
                  </button>
                </li>
              ))
            ) : (
              <li className="px-5 py-10 text-center text-sm text-sage">Nothing on fire today. Enjoy the quiet.</li>
            )}
          </ul>
        </div>

        <div className="lg:col-span-2 bg-white border border-forest/15 rounded-sm overflow-hidden" data-testid="dashboard-latest-signed">
          <header className="flex items-center justify-between px-5 py-3 border-b border-forest/10">
            <div>
              <h2 className="font-serif text-lg text-forest">Latest signed</h2>
              <p className="text-[11.5px] text-sage mt-0.5">Recently signed applications</p>
            </div>
          </header>
          <ul className="divide-y divide-forest/5">
            {data?.latest_signed?.length ? (
              data.latest_signed.map((f) => (
                <li key={f.id}>
                  <button
                    type="button"
                    data-testid={`dashboard-signed-${f.id}`}
                    onClick={() => onNavigate?.("customers", { focusId: f.customer_id, tab: "applications" })}
                    className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-cream-3 transition-colors"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <FileSignature size={14} className="text-emerald-700 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-forest truncate">{f.customer_name}</p>
                        <p className="text-[11.5px] text-sage mt-0.5 truncate">{f.type_label} · {formatDateDMY(f.signed_at)}</p>
                      </div>
                    </div>
                    <ChevronRight size={14} className="shrink-0 text-sage" />
                  </button>
                </li>
              ))
            ) : (
              <li className="px-5 py-8 text-center text-sm text-sage">No signed applications yet.</li>
            )}
          </ul>
        </div>
      </section>

      {/* Latest enquiries + outstanding invoices (kept so nothing is lost) */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-forest/15 rounded-sm overflow-hidden">
          <header className="flex items-center justify-between px-5 py-3 border-b border-forest/10">
            <div>
              <h2 className="font-serif text-lg text-forest">Latest enquiries</h2>
              <p className="text-[11.5px] text-sage mt-0.5">The five newest contact and WhatsApp leads</p>
            </div>
            <button
              data-testid="dashboard-view-all-enquiries"
              onClick={() => onNavigate?.("enquiries")}
              className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-gold-dark hover:text-forest"
            >View all</button>
          </header>
          <ul className="divide-y divide-forest/5">
            {data?.latest_enquiries?.length ? (
              data.latest_enquiries.map((e) => (
                <li key={`${e.source}-${e.id}`}>
                  <button
                    data-testid={`dashboard-enquiry-${e.id}`}
                    onClick={() => onNavigate?.("enquiries", { focusId: e.id, source: e.source })}
                    className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-cream-3 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {!e.read && <span className="inline-block w-1.5 h-1.5 rounded-full bg-gold" aria-label="Unread" />}
                        <p className="text-[13px] font-semibold text-forest truncate">{e.name}</p>
                      </div>
                      <p className="text-[11.5px] text-sage mt-0.5 truncate">{e.subject} · {formatDateDMY(e.created_at)}</p>
                    </div>
                    <ChevronRight size={14} className="shrink-0 text-sage" />
                  </button>
                </li>
              ))
            ) : (
              <li className="px-5 py-8 text-center text-sm text-sage">No enquiries yet.</li>
            )}
          </ul>
        </div>

        <div className="bg-white border border-forest/15 rounded-sm overflow-hidden">
          <header className="flex items-center justify-between px-5 py-3 border-b border-forest/10">
            <div>
              <h2 className="font-serif text-lg text-forest">Outstanding invoices</h2>
              <p className="text-[11.5px] text-sage mt-0.5">Any invoice with a balance due</p>
            </div>
            <button
              data-testid="dashboard-view-all-invoices"
              onClick={() => onNavigate?.("invoices")}
              className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-gold-dark hover:text-forest"
            >View all</button>
          </header>
          <ul className="divide-y divide-forest/5">
            {data?.overdue_invoices?.length ? (
              data.overdue_invoices.map((inv) => (
                <li key={inv.id}>
                  <button
                    data-testid={`dashboard-invoice-${inv.id}`}
                    onClick={() => onNavigate?.("invoices", { focusId: inv.id })}
                    className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-cream-3 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-semibold text-forest">{inv.number}</p>
                        <InvoiceStatusPill status={inv.derived_status} />
                      </div>
                      <p className="text-[11.5px] text-sage mt-0.5 truncate">{inv.customer_company || inv.customer_name || "—"} · Due {formatDateDMY(inv.due_date)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[13px] font-semibold text-forest font-mono">{formatGBP(inv.balance_due)}</p>
                      <p className="text-[10.5px] text-sage">of {formatGBP(inv.total)}</p>
                    </div>
                  </button>
                </li>
              ))
            ) : (
              <li className="px-5 py-8 text-center text-sm text-sage">Nothing outstanding. Nicely done.</li>
            )}
          </ul>
        </div>
      </section>
    </div>
  );
};
